// DataGrid — sortable / filterable / paginated table in the spirit of MUI X Data Grid, built on Radix +
// Tailwind. Client mode sorts, filters and paginates in memory; server mode only reports the models so the
// caller can query an API (e.g. cursor pagination).
import {
  Fragment,
  useCallback,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent,
  type PointerEvent as ReactPointerEvent,
  type ReactNode,
} from "react";
import {
  ArrowDown,
  ArrowUp,
  ChevronDown,
  ChevronFirst,
  ChevronLast,
  ChevronLeft,
  ChevronRight,
  MoreVertical,
} from "lucide-react";
import { cn } from "../../cn";
import { IconButton } from "../button";
import { NativeSelect } from "../field";
import { Menu } from "../overlay";
import { Skeleton } from "../spinner";
import { GRID_LOCALE_EN, type GridLocaleText } from "./locale";
import {
  cellValue,
  effectiveItems,
  filterRows,
  formatValue,
  operatorsFor,
  rowsToCsv,
  sortRows,
} from "./logic";
import { useGridLayout } from "./state";
import { ColumnsPanel, DensityMenu, ExportMenu, FilterPanel, QuickFilter } from "./toolbar";
import {
  EMPTY_FILTER_MODEL,
  type GridColDef,
  type GridDensity,
  type GridFilterItem,
  type GridFilterModel,
  type GridMode,
  type GridPaginationModel,
  type GridRowId,
  type GridSortModel,
} from "./types";

export interface DataGridProps<R> {
  /** Accessible name of the table. */
  label: string;
  rows: R[];
  columns: GridColDef<R>[];
  /** Persist column layout, density and page size in localStorage under `grid:<id>`. */
  id?: string;
  getRowId?: (row: R) => GridRowId;
  loading?: boolean;
  localeText?: GridLocaleText;
  /** client (default): everything in memory. server: the caller applies sort / filter / pagination. */
  mode?: GridMode;
  sortModel?: GridSortModel;
  onSortModelChange?: (m: GridSortModel) => void;
  /** Initial sort when uncontrolled. */
  defaultSortModel?: GridSortModel;
  filterModel?: GridFilterModel;
  onFilterModelChange?: (m: GridFilterModel) => void;
  paginationModel?: GridPaginationModel;
  onPaginationModelChange?: (m: GridPaginationModel) => void;
  pageSizeOptions?: number[];
  /** Server mode: total rows when known. */
  rowCount?: number;
  /** Server mode with cursors: whether a next page exists. */
  hasNextPage?: boolean;
  /** 0 disables pagination (client mode shows every row). */
  pagination?: boolean;
  checkboxSelection?: boolean;
  onRowSelectionModelChange?: (ids: GridRowId[]) => void;
  onRowClick?: (row: R) => void;
  /** Extra attributes on each `<tr>` (e.g. `data-testid`). */
  getRowAttributes?: (row: R) => Record<string, string | undefined>;
  /** Expandable detail panel below a row (MUI "master-detail"). */
  getDetailPanelContent?: (row: R) => ReactNode;
  /** Buttons at the right end of the toolbar (e.g. "Add user", reload). */
  toolbarActions?: ReactNode;
  hideToolbar?: boolean;
  hideFooter?: boolean;
  disableQuickFilter?: boolean;
  disableExport?: boolean;
  disableColumnFilter?: boolean;
  disableColumnReorder?: boolean;
  /** Only "and" between filters (e.g. the server cannot OR them). */
  disableFilterOr?: boolean;
  /** Hide the last-page button (cursor pagination cannot jump). */
  disableLastPage?: boolean;
  /** Server mode export: return every row to export (defaults to the rows on screen). */
  onExport?: () => Promise<R[]>;
  exportFileName?: string;
  /** Custom CSV (default: visible columns, formatted values). */
  toCsv?: (rows: R[]) => string;
  /** Rendered instead of the default "No rows" message (e.g. an empty state with an action). */
  noRowsContent?: ReactNode;
  defaultDensity?: GridDensity;
  className?: string;
}

const SYSTEM_WIDTH = 44;
const MIN_WIDTH = 64;
const DEFAULT_MIN = 110;
// Row padding per density ("standard" is the default).
const densityCell: Record<GridDensity, string> = { compact: "py-2.5", standard: "py-4", comfortable: "py-6" };

let filterSeq = 0;
const nextFilterId = () => `f${++filterSeq}`;

function download(name: string, text: string) {
  const url = URL.createObjectURL(new Blob([text], { type: "text/csv;charset=utf-8" }));
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  document.body.append(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/** Controlled when `value` is given, otherwise internal state. */
function useControllable<T>(value: T | undefined, initial: T, onChange?: (v: T) => void) {
  const [inner, setInner] = useState<T>(initial);
  const current = value !== undefined ? value : inner;
  const set = useCallback(
    (v: T) => {
      if (value === undefined) setInner(v);
      onChange?.(v);
    },
    [value, onChange],
  );
  return [current, set] as const;
}

export function DataGrid<R>(props: DataGridProps<R>) {
  const {
    label,
    rows,
    columns,
    id,
    loading,
    mode = "client",
    pageSizeOptions = [10, 25, 50, 100],
    checkboxSelection,
    onRowClick,
    getRowAttributes,
    getDetailPanelContent,
    toolbarActions,
    hideToolbar,
    hideFooter,
    disableQuickFilter,
    disableExport,
    disableColumnFilter,
    disableColumnReorder,
    pagination = true,
    className,
  } = props;
  const text = props.localeText ?? GRID_LOCALE_EN;
  const getRowId = props.getRowId;
  const rowId = useCallback(
    (r: R) => (getRowId ? getRowId(r) : String((r as { id?: unknown }).id)),
    [getRowId],
  );
  const layout = useGridLayout(id, columns, props.defaultDensity ?? "standard");
  const { columnState } = layout;

  const [sortModel, setSortModel] = useControllable(
    props.sortModel,
    props.defaultSortModel ?? [],
    props.onSortModelChange,
  );
  const [filterModel, setFilterModelRaw] = useControllable(
    props.filterModel,
    EMPTY_FILTER_MODEL,
    props.onFilterModelChange,
  );
  const defaultPageSize =
    layout.pageSize && pageSizeOptions.includes(layout.pageSize)
      ? layout.pageSize
      : pageSizeOptions.includes(25)
        ? 25
        : (pageSizeOptions[0] ?? 25);
  const [paging, setPaging] = useControllable(
    props.paginationModel,
    { page: 0, pageSize: defaultPageSize },
    props.onPaginationModelChange,
  );
  const [selected, setSelectedRaw] = useState<Set<GridRowId>>(new Set());
  const setSelected = (s: Set<GridRowId>) => {
    setSelectedRaw(s);
    props.onRowSelectionModelChange?.([...s]);
  };
  const [expanded, setExpanded] = useState<Set<GridRowId>>(new Set());
  const [panel, setPanel] = useState<"columns" | "filters" | null>(null);
  const [focus, setFocus] = useState<{ r: number; c: number }>({ r: 0, c: 0 });
  const [dragField, setDragField] = useState<string | null>(null);
  const [liveWidths, setLiveWidths] = useState<Record<string, number>>({});
  const headerRefs = useRef(new Map<string, HTMLTableCellElement>());
  const tableRef = useRef<HTMLTableElement>(null);

  const resetPage = () => {
    if (paging.page !== 0) setPaging({ ...paging, page: 0 });
  };
  const setFilterModel = (m: GridFilterModel) => {
    setFilterModelRaw(m);
    resetPage();
  };

  // ---------- columns ----------
  const byField = useMemo(() => new Map(columns.map((c) => [c.field, c])), [columns]);
  const width = (f: string) => liveWidths[f] ?? columnState.widths[f] ?? byField.get(f)?.width;
  const visible = columnState.order.filter((f) => byField.has(f) && !columnState.hidden.includes(f));
  const left = columnState.pinned.left.filter((f) => visible.includes(f));
  const right = columnState.pinned.right.filter((f) => visible.includes(f));
  const ordered = [...left, ...visible.filter((f) => !left.includes(f) && !right.includes(f)), ...right].map(
    (f) => byField.get(f)!,
  );
  const system = (getDetailPanelContent ? 1 : 0) + (checkboxSelection ? 1 : 0);

  const stickyStyle = (field: string): CSSProperties | undefined => {
    const li = left.indexOf(field);
    if (li >= 0) {
      const offset =
        system * SYSTEM_WIDTH + left.slice(0, li).reduce((s, f) => s + (width(f) ?? DEFAULT_MIN), 0);
      return { position: "sticky", left: offset, zIndex: 2 };
    }
    const ri = right.indexOf(field);
    if (ri >= 0) {
      const offset = right.slice(ri + 1).reduce((s, f) => s + (width(f) ?? DEFAULT_MIN), 0);
      return { position: "sticky", right: offset, zIndex: 2 };
    }
    return undefined;
  };
  const pinnedEdge = (field: string) =>
    field === left.at(-1)
      ? "shadow-[inset_-1px_0_0_var(--color-line),6px_0_8px_-6px_rgb(16_24_40/0.12)]"
      : field === right[0]
        ? "shadow-[inset_1px_0_0_var(--color-line),-6px_0_8px_-6px_rgb(16_24_40/0.12)]"
        : "";

  const pin = (field: string, side: "left" | "right" | null) => {
    const measured = headerRefs.current.get(field)?.getBoundingClientRect().width;
    layout.update((s) => ({
      ...s,
      widths: width(field) || !measured ? s.widths : { ...s.widths, [field]: Math.round(measured) },
      pinned: {
        left:
          side === "left"
            ? [...s.pinned.left.filter((f) => f !== field), field]
            : s.pinned.left.filter((f) => f !== field),
        right:
          side === "right"
            ? [field, ...s.pinned.right.filter((f) => f !== field)]
            : s.pinned.right.filter((f) => f !== field),
      },
    }));
  };
  const hide = (field: string, hidden: boolean) =>
    layout.update((s) => ({
      ...s,
      hidden: hidden ? [...new Set([...s.hidden, field])] : s.hidden.filter((f) => f !== field),
    }));

  // ---------- sorting ----------
  const sortOf = (field: string) => sortModel.find((s) => s.field === field)?.sort;
  const applySort = (field: string, dir: "asc" | "desc" | null, multi = false) => {
    const rest = multi ? sortModel.filter((s) => s.field !== field) : [];
    setSortModel(dir ? [...rest, { field, sort: dir }] : rest);
    resetPage();
  };
  const cycleSort = (field: string, multi: boolean) => {
    const cur = sortOf(field);
    applySort(field, cur === undefined ? "asc" : cur === "asc" ? "desc" : null, multi);
  };

  // ---------- filtering ----------
  const filterable = columns.filter((c) => c.filterable !== false && operatorsFor(c).length > 0);
  const newFilter = (field?: string): GridFilterItem | null => {
    const c = (field ? filterable.find((x) => x.field === field) : undefined) ?? filterable[0];
    return c ? { id: nextFilterId(), field: c.field, operator: operatorsFor(c)[0]! } : null;
  };
  const openFilterFor = (field: string) => {
    const existing = filterModel.items.find((i) => i.field === field);
    if (!existing) {
      const item = newFilter(field);
      if (item) setFilterModel({ ...filterModel, items: [...filterModel.items, item] });
    }
    setPanel("filters");
  };
  const filteredFields = new Set(effectiveItems(filterModel, columns).map((i) => i.field));

  // ---------- rows ----------
  const processed = useMemo(
    () => (mode === "server" ? rows : sortRows(filterRows(rows, filterModel, columns), sortModel, columns)),
    [mode, rows, filterModel, sortModel, columns],
  );
  const total = mode === "server" ? props.rowCount : processed.length;
  const pageRows =
    mode === "server" || !pagination
      ? processed
      : processed.slice(paging.page * paging.pageSize, (paging.page + 1) * paging.pageSize);
  const hasNext =
    mode === "server"
      ? (props.hasNextPage ?? (total !== undefined && (paging.page + 1) * paging.pageSize < total))
      : (paging.page + 1) * paging.pageSize < processed.length;
  const isFiltered =
    effectiveItems(filterModel, columns).length > 0 || filterModel.quickFilterValues.length > 0;

  // ---------- selection ----------
  const pageIds = pageRows.map(rowId);
  const allOnPage = pageIds.length > 0 && pageIds.every((i) => selected.has(i));
  const someOnPage = pageIds.some((i) => selected.has(i));
  const toggleRow = (idv: GridRowId) => {
    const n = new Set(selected);
    if (n.has(idv)) n.delete(idv);
    else n.add(idv);
    setSelected(n);
  };

  // ---------- export ----------
  const exportCsv = async (onlySelected: boolean) => {
    let data: R[];
    if (onlySelected) data = (mode === "server" ? rows : processed).filter((r) => selected.has(rowId(r)));
    else if (props.onExport) data = await props.onExport();
    else data = mode === "server" ? rows : processed;
    download(
      `${props.exportFileName ?? id ?? "export"}.csv`,
      props.toCsv ? props.toCsv(data) : rowsToCsv(data, ordered),
    );
  };

  // ---------- resize ----------
  const startResize = (field: string, e: ReactPointerEvent<HTMLElement>) => {
    e.preventDefault();
    e.stopPropagation();
    const th = headerRefs.current.get(field);
    const startX = e.clientX;
    const startW = th?.getBoundingClientRect().width ?? width(field) ?? DEFAULT_MIN;
    const min = Math.max(MIN_WIDTH, byField.get(field)?.minWidth ?? 0);
    let last = startW;
    const move = (ev: PointerEvent) => {
      last = Math.max(min, Math.round(startW + ev.clientX - startX));
      setLiveWidths({ [field]: last });
    };
    const up = () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
      setLiveWidths({});
      layout.update((s) => ({ ...s, widths: { ...s.widths, [field]: last } }));
    };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
  };
  const keyResize = (field: string, e: KeyboardEvent<HTMLElement>) => {
    if (e.key !== "ArrowLeft" && e.key !== "ArrowRight") return;
    e.preventDefault();
    const cur = width(field) ?? headerRefs.current.get(field)?.getBoundingClientRect().width ?? DEFAULT_MIN;
    const next = Math.max(MIN_WIDTH, Math.round(cur + (e.key === "ArrowRight" ? 16 : -16)));
    layout.update((s) => ({ ...s, widths: { ...s.widths, [field]: next } }));
  };

  // ---------- reorder ----------
  const dropOn = (target: string) => {
    if (!dragField || dragField === target) return;
    layout.update((s) => {
      const order = s.order.filter((f) => f !== dragField);
      order.splice(order.indexOf(target), 0, dragField);
      return { ...s, order };
    });
    setDragField(null);
  };

  // ---------- keyboard navigation (roving tab index over body cells) ----------
  const colCount = system + ordered.length;
  const onBodyKey = (e: KeyboardEvent<HTMLTableSectionElement>) => {
    if ((e.target as HTMLElement).closest("input,button,select,textarea,a") && e.key !== "Escape") {
      if (e.target !== e.currentTarget && (e.target as HTMLElement).tagName !== "TD") return;
    }
    let { r, c } = focus;
    if (e.key === "ArrowDown") r = Math.min(pageRows.length - 1, r + 1);
    else if (e.key === "ArrowUp") r = Math.max(0, r - 1);
    else if (e.key === "ArrowRight") c = Math.min(colCount - 1, c + 1);
    else if (e.key === "ArrowLeft") c = Math.max(0, c - 1);
    else if (e.key === "Home") c = e.ctrlKey ? ((r = 0), 0) : 0;
    else if (e.key === "End") c = e.ctrlKey ? ((r = pageRows.length - 1), colCount - 1) : colCount - 1;
    else if (e.key === "Enter" && onRowClick && pageRows[r]) {
      e.preventDefault();
      onRowClick(pageRows[r]!);
      return;
    } else if (e.key === " " && checkboxSelection && pageRows[r]) {
      e.preventDefault();
      toggleRow(rowId(pageRows[r]!));
      return;
    } else return;
    e.preventDefault();
    setFocus({ r, c });
    tableRef.current?.querySelector<HTMLElement>(`[data-cell="${r}:${c}"]`)?.focus();
  };

  const cellPad = cn("px-4", densityCell[layout.density]);
  const headerCell = "border-b border-line bg-subtle text-sm font-semibold text-ink whitespace-nowrap";
  const minTableWidth =
    system * SYSTEM_WIDTH + ordered.reduce((s, c) => s + (width(c.field) ?? c.minWidth ?? DEFAULT_MIN), 0);

  return (
    <div className={cn("min-w-0", className)} data-grid={id}>
      {!hideToolbar && (
        <div className="flex min-h-15 flex-wrap items-center gap-1 border-b border-line px-3 py-3">
          <ColumnsPanel
            columns={columns}
            state={columnState}
            text={text}
            open={panel === "columns"}
            onOpenChange={(o) => setPanel(o ? "columns" : null)}
            onToggle={(f, v) => hide(f, !v)}
            onShowAll={() => layout.update((s) => ({ ...s, hidden: [] }))}
            onHideAll={() =>
              layout.update((s) => ({
                ...s,
                hidden: columns.filter((c) => c.hideable !== false).map((c) => c.field),
              }))
            }
            onReset={layout.reset}
          />
          {!disableColumnFilter && (
            <FilterPanel
              columns={columns}
              model={filterModel}
              onChange={setFilterModel}
              text={text}
              open={panel === "filters"}
              onOpenChange={(o) => setPanel(o ? "filters" : null)}
              newItem={newFilter}
              disableOr={props.disableFilterOr}
            />
          )}
          <DensityMenu value={layout.density} onChange={layout.setDensity} text={text} />
          {!disableExport && (
            <ExportMenu onExport={(s) => void exportCsv(s)} selected={selected.size} text={text} />
          )}
          <div className="flex min-w-0 flex-1 items-center justify-end gap-2">
            {!disableQuickFilter && (
              <QuickFilter
                text={text}
                value={filterModel.quickFilterValues.join(" ")}
                onChange={(v) =>
                  setFilterModel({ ...filterModel, quickFilterValues: v.split(/\s+/).filter(Boolean) })
                }
              />
            )}
            {toolbarActions}
          </div>
        </div>
      )}

      <div className="relative overflow-x-auto">
        {loading && (
          <div
            role="progressbar"
            aria-label={text.loading}
            className="absolute inset-x-0 top-0 z-10 h-0.5 overflow-hidden bg-primary/15"
          >
            <div className="h-full w-1/3 animate-[grid-progress_1.1s_ease-in-out_infinite] bg-primary" />
          </div>
        )}
        <table
          ref={tableRef}
          aria-label={label}
          aria-busy={loading || undefined}
          className="w-full table-fixed border-separate border-spacing-0 text-left text-sm"
          style={{ minWidth: minTableWidth }}
        >
          <colgroup>
            {Array.from({ length: system }, (_, i) => (
              <col key={`s${i}`} style={{ width: SYSTEM_WIDTH }} />
            ))}
            {ordered.map((c) => (
              <col key={c.field} style={width(c.field) ? { width: width(c.field) } : undefined} />
            ))}
          </colgroup>
          <thead>
            <tr>
              {getDetailPanelContent && (
                <th
                  className={cn(headerCell, "sticky left-0 z-[3]")}
                  style={{ left: 0 }}
                  aria-label={text.expandRow}
                />
              )}
              {checkboxSelection && (
                <th
                  className={cn(headerCell, "sticky z-[3] px-3")}
                  style={{ left: getDetailPanelContent ? SYSTEM_WIDTH : 0 }}
                >
                  <input
                    type="checkbox"
                    aria-label={text.selectAll}
                    checked={allOnPage}
                    ref={(el) => {
                      if (el) el.indeterminate = !allOnPage && someOnPage;
                    }}
                    onChange={() => {
                      const n = new Set(selected);
                      for (const i of pageIds) {
                        if (allOnPage) n.delete(i);
                        else n.add(i);
                      }
                      setSelected(n);
                    }}
                  />
                </th>
              )}
              {ordered.map((c) => {
                const dir = sortOf(c.field);
                const sortable = c.sortable !== false && c.type !== "actions";
                const idx = sortModel.findIndex((s) => s.field === c.field);
                const align = c.align ?? (c.type === "number" ? "right" : "left");
                return (
                  <th
                    key={c.field}
                    ref={(el) => {
                      if (el) headerRefs.current.set(c.field, el);
                    }}
                    scope="col"
                    aria-sort={dir === "asc" ? "ascending" : dir === "desc" ? "descending" : undefined}
                    title={c.description}
                    draggable={!disableColumnReorder}
                    onDragStart={(e) => {
                      setDragField(c.field);
                      e.dataTransfer.effectAllowed = "move";
                    }}
                    onDragOver={(e) => {
                      if (dragField && dragField !== c.field) e.preventDefault();
                    }}
                    onDrop={() => dropOn(c.field)}
                    onDragEnd={() => setDragField(null)}
                    className={cn(
                      headerCell,
                      "group relative p-0",
                      stickyStyle(c.field) && "bg-subtle",
                      pinnedEdge(c.field),
                      dragField === c.field && "opacity-50",
                    )}
                    style={stickyStyle(c.field)}
                  >
                    <div
                      className={cn(
                        "flex h-full min-h-12 items-center gap-1 pr-1 pl-4",
                        align === "right" && "flex-row-reverse pr-4 pl-1",
                      )}
                    >
                      {sortable ? (
                        <button
                          type="button"
                          onClick={(e) => cycleSort(c.field, e.shiftKey || e.metaKey || e.ctrlKey)}
                          className={cn(
                            "flex min-w-0 items-center gap-1 rounded py-2 text-left hover:text-ink focus-visible:outline-offset-0",
                            align === "right" && "flex-row-reverse",
                            dir && "text-ink",
                          )}
                        >
                          <span className="truncate">{c.headerName}</span>
                          <span
                            aria-hidden
                            className={cn(
                              "inline-flex items-center text-ink-3 transition-opacity",
                              dir ? "opacity-100" : "opacity-0 group-hover:opacity-60",
                            )}
                          >
                            {dir === "desc" ? (
                              <ArrowDown className="size-4" />
                            ) : (
                              <ArrowUp className="size-4" />
                            )}
                            {sortModel.length > 1 && idx >= 0 && (
                              <sup className="text-[10px] font-semibold">{idx + 1}</sup>
                            )}
                          </span>
                        </button>
                      ) : (
                        <span className="truncate py-2">{c.headerName}</span>
                      )}
                      {filteredFields.has(c.field) && (
                        <span aria-hidden className="size-1.5 shrink-0 rounded-full bg-primary-dark" />
                      )}
                      <span className="flex-1" />
                      {c.type !== "actions" && (
                        <Menu.Root>
                          <Menu.Trigger asChild>
                            <button
                              type="button"
                              aria-label={text.columnMenu(c.headerName)}
                              className={cn(
                                "absolute top-1/2 z-[1] inline-flex size-7 -translate-y-1/2 items-center justify-center rounded bg-subtle text-ink-3 opacity-0 shadow-[0_0_0_4px_var(--color-subtle)] group-hover:opacity-100 hover:bg-hover hover:text-ink focus-visible:opacity-100 data-[state=open]:opacity-100",
                                align === "right" ? "left-1" : "right-2.5",
                              )}
                            >
                              <MoreVertical aria-hidden className="size-4" />
                            </button>
                          </Menu.Trigger>
                          <Menu.Content align="start">
                            {sortable && (
                              <>
                                <Menu.Item onSelect={() => applySort(c.field, "asc")}>
                                  <ArrowUp aria-hidden className="size-4 text-ink-3" />
                                  {text.columnMenuSortAsc}
                                </Menu.Item>
                                <Menu.Item onSelect={() => applySort(c.field, "desc")}>
                                  <ArrowDown aria-hidden className="size-4 text-ink-3" />
                                  {text.columnMenuSortDesc}
                                </Menu.Item>
                                {dir && (
                                  <Menu.Item onSelect={() => applySort(c.field, null, true)}>
                                    <span className="size-4" />
                                    {text.columnMenuUnsort}
                                  </Menu.Item>
                                )}
                                <Menu.Separator />
                              </>
                            )}
                            {!disableColumnFilter && c.filterable !== false && operatorsFor(c).length > 0 && (
                              <Menu.Item onSelect={() => openFilterFor(c.field)}>
                                {text.columnMenuFilter}
                              </Menu.Item>
                            )}
                            {c.hideable !== false && (
                              <Menu.Item onSelect={() => hide(c.field, true)}>
                                {text.columnMenuHide}
                              </Menu.Item>
                            )}
                            <Menu.Item onSelect={() => setPanel("columns")}>
                              {text.columnMenuManage}
                            </Menu.Item>
                            <Menu.Separator />
                            {!left.includes(c.field) && (
                              <Menu.Item onSelect={() => pin(c.field, "left")}>
                                {text.columnMenuPinLeft}
                              </Menu.Item>
                            )}
                            {!right.includes(c.field) && (
                              <Menu.Item onSelect={() => pin(c.field, "right")}>
                                {text.columnMenuPinRight}
                              </Menu.Item>
                            )}
                            {(left.includes(c.field) || right.includes(c.field)) && (
                              <Menu.Item onSelect={() => pin(c.field, null)}>
                                {text.columnMenuUnpin}
                              </Menu.Item>
                            )}
                          </Menu.Content>
                        </Menu.Root>
                      )}
                    </div>
                    {c.resizable !== false && (
                      <div
                        role="separator"
                        aria-orientation="vertical"
                        aria-label={text.columnResize(c.headerName)}
                        aria-valuenow={Math.round(width(c.field) ?? c.minWidth ?? DEFAULT_MIN)}
                        aria-valuemin={Math.max(MIN_WIDTH, c.minWidth ?? 0)}
                        aria-valuemax={1200}
                        tabIndex={0}
                        onPointerDown={(e) => startResize(c.field, e)}
                        onKeyDown={(e) => keyResize(c.field, e)}
                        onDoubleClick={() =>
                          layout.update((s) => {
                            const { [c.field]: _drop, ...widths } = s.widths;
                            return { ...s, widths };
                          })
                        }
                        className="absolute top-2 right-0 bottom-2 z-[1] w-2 cursor-col-resize touch-none after:absolute after:inset-y-0 after:right-0.5 after:w-px after:bg-line hover:after:w-0.5 hover:after:bg-primary focus-visible:after:bg-primary"
                      />
                    )}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody onKeyDown={onBodyKey}>
            {loading && pageRows.length === 0
              ? Array.from({ length: 5 }, (_, i) => (
                  <tr key={`sk${i}`}>
                    <td colSpan={colCount} className={cn(cellPad, "border-b border-line")}>
                      <Skeleton className="h-5" />
                    </td>
                  </tr>
                ))
              : pageRows.map((row, r) => {
                  const idv = rowId(row);
                  const open = expanded.has(idv);
                  const isSel = selected.has(idv);
                  const detail = open && getDetailPanelContent ? getDetailPanelContent(row) : null;
                  let c = 0;
                  const cellProps = () => {
                    const ci = c++;
                    return {
                      "data-cell": `${r}:${ci}`,
                      tabIndex: focus.r === r && focus.c === ci ? 0 : -1,
                      onFocus: () => setFocus({ r, c: ci }),
                    };
                  };
                  return (
                    <Fragment key={idv}>
                      <tr
                        {...(getRowAttributes?.(row) ?? {})}
                        aria-selected={checkboxSelection ? isSel : undefined}
                        onClick={
                          onRowClick
                            ? (e) => {
                                if ((e.target as HTMLElement).closest("button,a,input,select,label")) return;
                                onRowClick(row);
                              }
                            : undefined
                        }
                        className={cn(
                          "group/row",
                          onRowClick && "cursor-pointer",
                          isSel ? "bg-info-bg" : "bg-paper hover:bg-subtle",
                        )}
                      >
                        {getDetailPanelContent && (
                          <td
                            {...cellProps()}
                            className="sticky left-0 z-[1] border-b border-line bg-inherit px-1.5"
                          >
                            <IconButton
                              label={open ? text.collapseRow : text.expandRow}
                              aria-expanded={open}
                              className="size-8"
                              onClick={() => {
                                const n = new Set(expanded);
                                if (open) n.delete(idv);
                                else n.add(idv);
                                setExpanded(n);
                              }}
                            >
                              {open ? (
                                <ChevronDown aria-hidden className="size-4" />
                              ) : (
                                <ChevronRight aria-hidden className="size-4" />
                              )}
                            </IconButton>
                          </td>
                        )}
                        {checkboxSelection && (
                          <td
                            {...cellProps()}
                            className="sticky z-[1] border-b border-line bg-inherit px-3"
                            style={{ left: getDetailPanelContent ? SYSTEM_WIDTH : 0 }}
                          >
                            <input
                              type="checkbox"
                              aria-label={text.selectRow}
                              checked={isSel}
                              onChange={() => toggleRow(idv)}
                            />
                          </td>
                        )}
                        {ordered.map((col) => {
                          const value = cellValue(col, row);
                          const formatted = formatValue(col, row, value);
                          const align = col.align ?? (col.type === "number" ? "right" : "left");
                          const sticky = stickyStyle(col.field);
                          return (
                            <td
                              key={col.field}
                              {...cellProps()}
                              style={sticky}
                              className={cn(
                                cellPad,
                                "overflow-hidden border-b border-line align-middle text-ellipsis focus-visible:outline-offset-[-2px]",
                                align === "right" && "text-right",
                                align === "center" && "text-center",
                                sticky && cn("bg-inherit", pinnedEdge(col.field)),
                                col.cellClassName,
                              )}
                            >
                              {col.renderCell ? col.renderCell({ row, value, formatted }) : formatted || "—"}
                            </td>
                          );
                        })}
                      </tr>
                      {detail !== null && detail !== undefined && (
                        <tr data-detail-panel>
                          <td colSpan={colCount} className="border-b border-line bg-subtle px-6 py-4">
                            {detail}
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
          </tbody>
        </table>
        {!loading && pageRows.length === 0 && (
          <div role="status" className="px-6 py-14 text-center">
            {isFiltered ? (
              <p className="text-sm text-ink-2">{text.noResults}</p>
            ) : (
              (props.noRowsContent ?? <p className="text-sm text-ink-2">{text.noRows}</p>)
            )}
          </div>
        )}
      </div>

      {!hideFooter && (pagination || selected.size > 0) && (
        <div className="flex flex-wrap items-center justify-between min-h-15 gap-3 px-4 py-3 text-sm text-ink-2">
          <span aria-live="polite">{selected.size > 0 ? text.selectedRows(selected.size) : ""}</span>
          {pagination && (
            <div className="flex flex-wrap items-center gap-3">
              <label className="flex items-center gap-2">
                {text.rowsPerPage}
                <NativeSelect
                  className="h-9 w-20"
                  value={paging.pageSize}
                  onChange={(e) => {
                    const size = Number(e.target.value);
                    layout.setPageSize(size);
                    setPaging({ page: 0, pageSize: size });
                  }}
                >
                  {pageSizeOptions.map((n) => (
                    <option key={n} value={n}>
                      {n}
                    </option>
                  ))}
                </NativeSelect>
              </label>
              <span className="tabular-nums" data-testid="grid-displayed-rows">
                {pageRows.length === 0
                  ? text.displayedRows(0, 0, total ?? 0, false)
                  : text.displayedRows(
                      paging.page * paging.pageSize + 1,
                      paging.page * paging.pageSize + pageRows.length,
                      total,
                      hasNext,
                    )}
              </span>
              <div className="flex items-center">
                {total !== undefined && (
                  <IconButton
                    label={text.firstPage}
                    className="size-9"
                    disabled={paging.page === 0}
                    onClick={() => setPaging({ ...paging, page: 0 })}
                  >
                    <ChevronFirst aria-hidden className="size-4" />
                  </IconButton>
                )}
                <IconButton
                  label={text.previousPage}
                  className="size-9"
                  disabled={paging.page === 0}
                  onClick={() => setPaging({ ...paging, page: paging.page - 1 })}
                >
                  <ChevronLeft aria-hidden className="size-4" />
                </IconButton>
                <IconButton
                  label={text.nextPage}
                  className="size-9"
                  disabled={!hasNext}
                  onClick={() => setPaging({ ...paging, page: paging.page + 1 })}
                >
                  <ChevronRight aria-hidden className="size-4" />
                </IconButton>
                {total !== undefined && !props.disableLastPage && (
                  <IconButton
                    label={text.lastPage}
                    className="size-9"
                    disabled={!hasNext}
                    onClick={() =>
                      setPaging({ ...paging, page: Math.max(0, Math.ceil(total / paging.pageSize) - 1) })
                    }
                  >
                    <ChevronLast aria-hidden className="size-4" />
                  </IconButton>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
